import * as React from 'react';
import { I18n } from 'react-redux-i18n';
import cx from 'classnames';
import { FixedSizeList as List } from 'react-window';

import { Checkbox } from '@common/components/checkbox';
import { getTestAttributes } from '@common/utils/tests';
import { Ellipsis } from '@common/components/Ellipsis';
import { Flex } from '@common/components/Flex';

import { MODULE_NAME } from '../../database-security';

import './folder-security-settings.styles.scss';

const nameField = 'name';

export const FolderSecuritySettings = (props) => {
  const { className, groups, databaseSecurity, onDatabaseSecurityChange } = props;
  const columns = React.useMemo(() => [
    { name: 'Class Name', fieldName: nameField },
    { name: 'Add Fold?', fieldName: 'addFolderSec' },
    { name: 'Del Fold?', fieldName: 'delFolderSec' },
    { name: 'Modify Fold?', fieldName: 'modFolderSec' },
    { name: 'Add D Fold?', fieldName: 'addDataFolderSec' },
    { name: 'Del D Fold?', fieldName: 'delDataFolderSec' },
    { name: 'Modify D Fold?', fieldName: 'modDataFolderSec' },
  ].map((x) => ({ ...x, name: I18n.t(x.name) })), []);

  const cellWidth = `${100/columns.length}%`;

  const isChecked = React.useCallback((userGroup, featureFieldName) => {
    const config = databaseSecurity[userGroup.secIndex];
    const featureSecPosition = config ? config[featureFieldName] : null;

    if (!userGroup.secPosition === null || featureSecPosition === null) {
      return false;
    }

    return (featureSecPosition & (1 << (userGroup.secPosition - 1))) > 0;
  }, [databaseSecurity]);

  // SP-10665: below code gives aplhabetically sorted (based on 'name') groupsList and keeps 'All Users' always on top.
  const groupsList = React.useMemo(() => {
    return Object.values(groups).toSorted((a, b) => {
      if (a.name === "All Users") return -1; // Keep 'All Users' on top
      if (b.name === "All Users") return 1;
      return a.name.localeCompare(b.name); // Sort others alphabetically
    });
  }, [groups]);

  const handleChange = React.useCallback((e) => {
    e.persist();
    
    const { dataset: { featureField, groupId }, checked } = e.target;
    const group = groups[groupId];
    const config = databaseSecurity[group.secIndex];
    let featureSecPosition = config[featureField];

    if (checked) {
      featureSecPosition = featureSecPosition | (1 << (group.secPosition - 1));
    } else {
      featureSecPosition = featureSecPosition & ~(1 << (group.secPosition - 1));
    }

    const newDatabaseSecurity = {
      ...databaseSecurity,
      [group.secIndex]: {
        ...config,
        [featureField]: featureSecPosition
      },
    };

    onDatabaseSecurityChange(newDatabaseSecurity);
  }, [groups, databaseSecurity, onDatabaseSecurityChange]);

  const renderGroup = React.useCallback(({ index, data, style }) => {
    const item = data[index];
    
    return (
      <Flex 
        justify="flex-start"
        alignItems="center"
        style={style}
        className="FolderSecuritySettings__groups-list__item"
        {...getTestAttributes(MODULE_NAME, 'security-settings-group', 'item')}
      >
        {columns.map((x, i) => {
          if (x.fieldName === nameField) {
            return (
              <Ellipsis 
                width={cellWidth} 
                text={item.name} 
                key={i} 
                {...getTestAttributes(MODULE_NAME, 'security-settings-group', 'text', x.fieldName)}
              />
            );
          }

          return (
            <input 
              {...getTestAttributes(MODULE_NAME, 'security-settings-group', 'checkbox', x.fieldName)}
              key={i} 
              style={{ width: cellWidth }} 
              type="checkbox" 
              checked={isChecked({ secIndex: item.secIndex, secPosition: item.secPosition }, x.fieldName)}
              data-feature-field={x.fieldName}
              data-group-id={item.id}
              onChange={handleChange}
            />
          );
        })}
      </Flex>  
    );
  }, [isChecked, handleChange]);

  const getGroupKey = React.useCallback((index, data) => {
    return data[index].id;
  }, []);

  return (
    <section className={cx('FolderSecuritySettings', className)}>
      <h5 className="FolderSecuritySettings__title">{I18n.t('Folder Security Settings')}</h5>
      <Flex className="FolderSecuritySettings__groups-header" width="100%" justify="flex-start">
        {columns.map((x) => (
          <Ellipsis className="FolderSecuritySettings__groups-header__item" width={cellWidth} text={x.name} key={x.name} />
        ))}
      </Flex>
      <List
        className="FolderSecuritySettings__groups-list"
        height={200}
        itemCount={groupsList.length}
        itemSize={26}
        width="100%"
        overscanCount={5}
        itemData={groupsList}
        itemKey={getGroupKey}
      >
        {renderGroup}
      </List>
    </section>
  );
};